// Bridge back to the NetSuite Sales Order page that launched cpq2.
//
// The SO client script opens cpq2 with window.open(...), so window.opener is the
// SO page. The order is typically UNSAVED at this point, so we can't write to it
// via the API — instead we postMessage the configured line to the opener, whose
// client script adds/updates the line on the in-memory record (currentRecord).
//
// We post with targetOrigin "*" because we can't read the opener's cross-origin
// location; the receiver authenticates by checking event.origin === cpq2's URL.

import { getLaunchContext } from "./launchContext";
import type { PriorLineDescriptions } from "./priorLines";

export const CONFIGURED_LINE_AMOUNT = 100;

/** True when cpq2 was opened from another window (the Sales Order page). */
export function openedFromSalesOrder(): boolean {
  return typeof window !== "undefined" && !!window.opener && window.opener !== window;
}

// A second SO line the configured item drags along: the customer-specific
// "Serial Tag". The opener adds it once (dedup by item id) with this per-config
// description, qty 1, $0 — and on a second, DIFFERENT config on the same order it
// leaves the existing tag line alone rather than rewriting it. See
// src/util/serialTag.ts.
export type SerialTagLinePayload = {
  itemId: string;
  itemName: string;
  description: string;
  quantity: number;
  amount: number;
  taxCode: string;
  // What cpq2 generated for this line LAST time (Edit Config), so the order can merge
  // instead of overwrite. See src/util/priorLines.ts.
  priorDescription?: string;
};

// Extra items the configured product drags onto the order as their own lines,
// each added once (dedup by item id) with qty and NetSuite's native price + tax.
// See src/util/additionalLines.ts (Flange Gasket, Ext Nipple, Main Burner, Nozzle).
// `description` is an optional per-config line-description override — set for the
// Main Burner / mixer and the Flange Gasket, whose stored descriptions carry a
// labeled template cpq2 fills from the config (fillMixerDescription /
// fillGasketDescription). The Ext Nipple and Nozzle keep their native description.
export type AdditionalLinePayload = {
  itemId: string;
  itemName: string;
  quantity: number;
  description?: string;
  priorDescription?: string; // as on SerialTagLinePayload
};

// Sales-order BODY fields this configuration wants set, as { fieldId: text }.
//
// cpq2 cannot READ the order (it only has the launch params on the URL), and per Kevin
// it must not fetch the order through the proxy to find out. It does not need to: the
// receiving client script is running ON that order, so it reads the current value
// itself and merges. See the write-back for the merge rule.
//
// Today this carries only custbodyshipping_instructions, from the Flame Cell Shipping
// Instructions operation. Kept as a generic map rather than one named field so the
// next product that needs a body field does not need a message-shape change.
export type OrderFieldWrites = Record<string, string>;

export type ConfiguredLineMessage = {
  source: "cpq2";
  type: "configured-line";
  itemId: string;
  itemName: string;
  amount: number;
  quantity: number;
  line: number | null; // 1-based SO line for "Edit Config"; null = add a new line
  // Configured line's description (the built Sales Description). Set so the
  // write-back can refresh the SO line's description on Edit Config (where the
  // existing line would otherwise keep its stale text).
  description: string | null;
  // The Sales Description cpq2 generated LAST time (Edit Config only), so the order merges a
  // person's edits to the line's description instead of replacing them. Null: replace.
  priorDescription: string | null;
  // The lines the configuration brought LAST time (Edit Config): its Serial Tag and every
  // add-on. The order moves this part's quantity off the lines the edit dropped, and names
  // the ones no part needs any more. Null: not known (a new config, a legacy item, a failed
  // lookup), and the order then changes no quantities for a dropped line.
  priorExtras: { label: "serialTag" | "additional"; itemId: string; itemName: string; description: string | null }[] | null;
  // Customer Part Number for this config, set DIRECTLY on the configured item's
  // SO line (we don't rely on NetSuite sourcing it from the
  // customrecord_scm_customerpartnumber record, which raced with that record's
  // creation and left the column blank). Both are null when none was entered.
  //   customerPartNumberId — the CPN record's INTERNAL id, for the select column
  //     custcol_scm_customerpartnumber (which stores the record reference).
  //   customerPartNumber   — the typed part-number TEXT, for the display column.
  customerPartNumberId: string | null;
  customerPartNumber: string | null;
  tranid: string | null;
  customerid: string | null;
  custbody101: string | null;
  custbody102: string | null;
  serialTag: SerialTagLinePayload | null;
  additionalLines: AdditionalLinePayload[];
  // Body fields to set on the order itself (see OrderFieldWrites). {} when none.
  orderFields: OrderFieldWrites;
};

/** The baseline's lines in the order they would sit on the order. Its Serial Tag is the
 *  customer's tag item, the same one this submit resolved, so it takes that id. */
function priorExtrasFor(
  prior: PriorLineDescriptions | null,
  serialTag: SerialTagLinePayload | null
): ConfiguredLineMessage["priorExtras"] {
  if (!prior || !prior.additionalLines) return null;
  const out: NonNullable<ConfiguredLineMessage["priorExtras"]> = [];
  if (serialTag && prior.serialTag != null) {
    out.push({ label: "serialTag", itemId: serialTag.itemId, itemName: serialTag.itemName, description: prior.serialTag });
  }
  for (const l of prior.additionalLines) out.push({ label: "additional", ...l });
  return out;
}

/**
 * Send the just-configured item back to the Sales Order so its client script can
 * add/update the line (amount = $100). When `serialTag` is provided, the opener
 * also adds the customer's Serial Tag line (once). Returns false when not opened
 * from an SO.
 *
 * `opts.line` overrides which SO line to target: pass the launched line (Edit
 * Config) to update it in place, or `null` to add a new line (New Config — e.g.
 * a locked item or a loaded configuration). When omitted, the launch context's
 * line is used.
 */
export function postConfiguredLine(
  item: { itemId: string | number; itemName?: string },
  opts?: {
    serialTag?: SerialTagLinePayload | null;
    additionalLines?: AdditionalLinePayload[];
    description?: string | null;
    customerPartNumberId?: string | null;
    customerPartNumber?: string | null;
    line?: number | null;
    // Sales price from the NDB price list (src/util/ndbPricing.ts). When omitted
    // the line keeps the CONFIGURED_LINE_AMOUNT placeholder — which is what
    // happens whenever the price-list keys don't resolve to a single price.
    amount?: number | null;
    // Order body fields to set (see OrderFieldWrites).
    orderFields?: OrderFieldWrites | null;
    // What cpq2 generated for these lines last time (Edit Config). See util/priorLines.
    prior?: PriorLineDescriptions | null;
  }
): boolean {
  if (!openedFromSalesOrder()) return false;
  const ctx = getLaunchContext();
  const prior = opts?.prior ?? null;
  const msg: ConfiguredLineMessage = {
    source: "cpq2",
    type: "configured-line",
    itemId: String(item.itemId),
    itemName: item.itemName ?? "",
    amount:
      opts?.amount != null && Number.isFinite(opts.amount) && opts.amount > 0
        ? opts.amount
        : CONFIGURED_LINE_AMOUNT,
    quantity: 1,
    line: opts && opts.line !== undefined ? opts.line : ctx.line,
    description: opts?.description ?? null,
    priorDescription: prior?.configured ?? null,
    priorExtras: priorExtrasFor(prior, opts?.serialTag ?? null),
    customerPartNumberId: opts?.customerPartNumberId ?? null,
    customerPartNumber: opts?.customerPartNumber ?? null,
    tranid: ctx.transactionId,
    customerid: ctx.customerId,
    custbody101: ctx.custbody101,
    custbody102: ctx.custbody102,
    serialTag:
      opts?.serialTag && prior?.serialTag != null
        ? { ...opts.serialTag, priorDescription: prior.serialTag }
        : opts?.serialTag ?? null,
    additionalLines: (opts?.additionalLines ?? []).map((l) =>
      prior && prior.additional[l.itemId] != null ? { ...l, priorDescription: prior.additional[l.itemId] } : l
    ),
    orderFields: opts?.orderFields ?? {},
  };
  try {
    // Logged so the write-back can be diagnosed from the cpq2 console.
    console.log("[cpq2] posting configured-line to opener:", msg);
    window.opener!.postMessage(msg, "*");
    return true;
  } catch {
    return false;
  }
}
